import React from 'react';
import styled, { css } from 'styled-components';

import useTranslation from 'hooks/use-translation';
import { Typography } from 'src/typography';

import type { EducationSectionProps } from '../education-section-props';
import { DesktopEducationSection } from '../desktop-education-section';

export function HowItWorks({ variant = 'mobile' }: EducationSectionProps): JSX.Element {
  const { t } = useTranslation();
  const isDesktop = variant === 'desktop';

  const steps = [
    {
      title: t('dutchiePay.signup.howItWorks.step1.title', 'Create account'),
      description: t('dutchiePay.signup.howItWorks.step1.description', 'Sign in with your email or social account.'),
    },
    {
      title: t('dutchiePay.signup.howItWorks.step2.title', 'Link your bank'),
      description: t('dutchiePay.signup.howItWorks.step2.description', 'Securely connect your bank.'),
    },
    {
      title: t('dutchiePay.signup.howItWorks.step3.title', 'Instant checkout'),
      description: t('dutchiePay.signup.howItWorks.step3.description', 'Pay seamlessly for all future orders.'),
    },
  ];

  const content = (
    <StepList $showConnector={!isDesktop}>
      {steps.map((step, index) => (
        <Step key={step.title}>
          <StepNumber aria-hidden='true'>{index + 1}</StepNumber>
          <StepText>
            <StepTitle tag='span' size={isDesktop ? 'large' : 'small'}>
              {step.title}
            </StepTitle>
            <StepDescription tag='span' size={isDesktop ? 'large' : 'small'}>
              {step.description}
            </StepDescription>
          </StepText>
        </Step>
      ))}
    </StepList>
  );

  if (isDesktop) {
    return (
      <DesktopEducationSection
        eyebrow={t('dutchiePay.signup.howItWorks.eyebrow', 'How Pay by Bank works')}
        title={t('dutchiePay.signup.howItWorks.desktopTitle', 'Link once. Pay in one tap.')}
        titleId='pbb-how-it-works-title'
      >
        {content}
      </DesktopEducationSection>
    );
  }

  return (
    <Card aria-labelledby='pbb-how-it-works-title'>
      <SectionTitle tag='h2' size='large' id='pbb-how-it-works-title'>
        {t('dutchiePay.signup.howItWorks.title', 'How it works')}
      </SectionTitle>
      {content}
    </Card>
  );
}

const Card = styled.section`
  background-color: var(--dcm-pbb-how-it-works-card-background, ${({ theme }) => theme.colors.green10});
  border-radius: var(--dcm-pbb-how-it-works-card-border-radius, 10px);
  padding: 24px 16px;
`;

const SectionTitle = styled(Typography.Title)`
  display: block;
  margin: 0 0 16px 0;
  color: var(--dcm-pbb-how-it-works-title-color, ${({ theme }) => theme.colors.dutchiePayBlack});
`;

const StepList = styled.ol<{ $showConnector: boolean }>`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;

  ${({ $showConnector }) =>
    $showConnector &&
    css`
      &::after {
        background-color: var(--dcm-pbb-how-it-works-step-connector-color, ${({ theme }) => theme.colors.grey[85]});
        content: '';
        height: calc(100% - 24px);
        left: 13px;
        position: absolute;
        top: 8px;
        width: 2px;
        z-index: 0;
      }
    `}
`;

const Step = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const StepNumber = styled.span`
  z-index: 1;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background-color: var(--dcm-pbb-how-it-works-step-number-background, ${({ theme }) => theme.colors.white});
  border: 1px solid var(--dcm-pbb-how-it-works-step-number-border-color, ${({ theme }) => theme.colors.dutchiePayGreen});
  border-radius: var(--dcm-pbb-how-it-works-step-number-border-radius, 50%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  color: var(--dcm-pbb-how-it-works-step-number-color, ${({ theme }) => theme.colors.dutchiePayGreen});
`;

const StepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StepTitle = styled(Typography.Title)`
  color: var(--dcm-pbb-how-it-works-step-title-color, ${({ theme }) => theme.colors.dutchiePayBlack});
`;

const StepDescription = styled(Typography.Body)`
  color: var(--dcm-pbb-how-it-works-step-description-color, ${({ theme }) => theme.colors.grey[35]});
`;
